package fr.frux.feature.treedetail.identity

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Description
import androidx.compose.material.icons.outlined.LocationOn
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import fr.frux.core.common.format.formatCoordinates
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.TreeGlyphDisc
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.core.designsystem.icons.treeMarkerTint
import fr.frux.feature.treedetail.TreeDetail
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_certainty_certain
import fr.frux.feature.treedetail.resources.treedetail_certainty_probable
import fr.frux.feature.treedetail.resources.treedetail_certainty_to_confirm
import fr.frux.feature.treedetail.resources.treedetail_identity_add_note
import fr.frux.feature.treedetail.resources.treedetail_identity_correct
import fr.frux.feature.treedetail.resources.treedetail_identity_edit_note
import fr.frux.feature.treedetail.resources.treedetail_identity_move
import fr.frux.feature.treedetail.resources.treedetail_identity_note_empty
import fr.frux.feature.treedetail.resources.treedetail_identity_note_label
import fr.frux.feature.treedetail.resources.treedetail_identity_position_label
import fr.frux.feature.treedetail.resources.treedetail_identity_species_label
import fr.frux.feature.treedetail.resources.treedetail_identity_subtitle
import fr.frux.feature.treedetail.resources.treedetail_identity_title
import fr.frux.shared.domain.model.Certainty
import org.jetbrains.compose.resources.stringResource

private val ROW_MIN_HEIGHT = 72.dp
private val DISC_SIZE = 40.dp
private const val NOTE_MAX_LINES = 4

/** Décalage des filets : marge de 16 dp, disque de 40 dp et 12 dp d'espacement. */
private val DIVIDER_INDENT = 68.dp

/**
 * Bloc Identité de la fiche (canvas `Fiche`) : espèce et certitude, position et note, chacune avec
 * son action, puis le pied de la dernière correction et l'accès à l'historique. Les actions sont
 * visibles de tous : sans compte, c'est l'écran qui ouvre l'invite de connexion.
 */
@Composable
internal fun IdentityBlock(
    tree: TreeDetail,
    ui: IdentityUi,
    onCorrectSpecies: () -> Unit,
    onMove: () -> Unit,
    onEditNote: () -> Unit,
    onHistory: () -> Unit,
    modifier: Modifier = Modifier,
    /** Ligne « Lieu » (ADR 0035), sous la position. Absente des usages qui ne la fournissent pas. */
    siteRow: (@Composable () -> Unit)? = null,
) {
    val scheme = MaterialTheme.colorScheme
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        color = scheme.surface,
        border = BorderStroke(1.dp, scheme.outlineVariant),
        shadowElevation = 1.dp,
    ) {
        Column {
            Column(
                modifier = Modifier.padding(start = 16.dp, top = 16.dp, end = 16.dp, bottom = 4.dp),
                verticalArrangement = Arrangement.spacedBy(2.dp),
            ) {
                Text(
                    text = stringResource(Res.string.treedetail_identity_title),
                    style = MaterialTheme.typography.titleLarge,
                    modifier = Modifier.semantics { heading() },
                )
                Text(
                    text = stringResource(Res.string.treedetail_identity_subtitle),
                    style = MaterialTheme.typography.bodyMedium,
                    color = scheme.onSurfaceVariant,
                )
            }
            SpeciesRow(tree, highlighted = ui.highlighted == IdentityField.SPECIES, onCorrect = onCorrectSpecies)
            IdentityDivider()
            PositionRow(tree, highlighted = ui.highlighted == IdentityField.POSITION, onMove = onMove)
            IdentityDivider()
            if (siteRow != null) {
                siteRow()
                IdentityDivider()
            }
            NoteRow(tree, highlighted = ui.highlighted == IdentityField.NOTE, onEdit = onEditNote)
            IdentityFooterRow(tree = tree, correctedByMe = ui.correctedByMe, onHistory = onHistory)
        }
    }
}

@Composable
private fun IdentityDivider() {
    HorizontalDivider(
        modifier = Modifier.padding(start = DIVIDER_INDENT),
        color = MaterialTheme.colorScheme.outlineVariant,
    )
}

/** Ligne commune : disque, libellé au-dessus de la valeur, action à droite. */
@Composable
private fun IdentityRow(
    highlighted: Boolean,
    leading: @Composable () -> Unit,
    label: String,
    actionLabel: String,
    onAction: () -> Unit,
    value: @Composable () -> Unit,
) {
    val scheme = MaterialTheme.colorScheme
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .background(if (highlighted) scheme.primaryContainer else Color.Transparent)
                .heightIn(min = ROW_MIN_HEIGHT)
                .padding(PaddingValues(start = 16.dp, top = 12.dp, end = 6.dp, bottom = 12.dp)),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        leading()
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(1.dp)) {
            Text(text = label, style = MaterialTheme.typography.bodyMedium, color = scheme.onSurfaceVariant)
            value()
        }
        FruxButton(text = actionLabel, onClick = onAction, style = FruxButtonStyle.Text)
    }
}

@Composable
private fun SpeciesRow(
    tree: TreeDetail,
    highlighted: Boolean,
    onCorrect: () -> Unit,
) {
    IdentityRow(
        highlighted = highlighted,
        leading = {
            TreeGlyphDisc(
                glyph = tree.speciesGlyph ?: FruxSpeciesGlyphs.Fleur,
                tint = treeMarkerTint(tree.scientificName, tree.status),
                size = DISC_SIZE,
            )
        },
        label = stringResource(Res.string.treedetail_identity_species_label),
        actionLabel = stringResource(Res.string.treedetail_identity_correct),
        onAction = onCorrect,
    ) {
        Column {
            Text(text = tree.speciesName, style = MaterialTheme.typography.titleMedium)
            CertaintyChip(tree.certainty, modifier = Modifier.padding(top = 4.dp))
        }
    }
}

@Composable
private fun CertaintyChip(
    certainty: Certainty,
    modifier: Modifier = Modifier,
) {
    val label =
        when (certainty) {
            Certainty.CERTAIN -> stringResource(Res.string.treedetail_certainty_certain)
            Certainty.PROBABLE -> stringResource(Res.string.treedetail_certainty_probable)
            Certainty.TO_CONFIRM -> stringResource(Res.string.treedetail_certainty_to_confirm)
        }
    Box(
        modifier =
            modifier
                .clip(RoundedCornerShape(50))
                .background(MaterialTheme.colorScheme.surfaceVariant)
                .heightIn(min = 22.dp)
                .padding(horizontal = 8.dp),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun PositionRow(
    tree: TreeDetail,
    highlighted: Boolean,
    onMove: () -> Unit,
) {
    IdentityRow(
        highlighted = highlighted,
        leading = { IconDisc(Icons.Outlined.LocationOn) },
        label = stringResource(Res.string.treedetail_identity_position_label),
        actionLabel = stringResource(Res.string.treedetail_identity_move),
        onAction = onMove,
    ) {
        Text(
            text = formatCoordinates(tree.latitude, tree.longitude),
            style = MaterialTheme.typography.titleMedium,
        )
    }
}

@Composable
private fun NoteRow(
    tree: TreeDetail,
    highlighted: Boolean,
    onEdit: () -> Unit,
) {
    val note = tree.description?.takeIf { it.isNotBlank() }
    IdentityRow(
        highlighted = highlighted,
        leading = { IconDisc(Icons.Outlined.Description) },
        label = stringResource(Res.string.treedetail_identity_note_label),
        actionLabel =
            stringResource(
                if (note != null) Res.string.treedetail_identity_edit_note else Res.string.treedetail_identity_add_note,
            ),
        onAction = onEdit,
    ) {
        if (note != null) {
            Text(
                text = note,
                style = MaterialTheme.typography.titleMedium,
                maxLines = NOTE_MAX_LINES,
                overflow = TextOverflow.Ellipsis,
            )
        } else {
            Text(
                text = stringResource(Res.string.treedetail_identity_note_empty),
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

@Composable
private fun IconDisc(icon: ImageVector) {
    Box(
        modifier = Modifier.size(DISC_SIZE).background(MaterialTheme.colorScheme.surfaceVariant, CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.primary,
            modifier = Modifier.size(20.dp),
        )
    }
}
